import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { VoicemailQueueStatus } from '@callhub/shared';
import { api } from '@/lib/api';
import { buildDialerPath } from '@/lib/routes';

type QueueFilter = 'all' | VoicemailQueueStatus;

const FILTERS: Array<{ value: QueueFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'completed', label: 'Completed' },
];

function statusLabel(status: VoicemailQueueStatus) {
  if (status === 'reviewed') return 'Reviewed voicemail';
  if (status === 'completed') return 'Completed voicemail';
  return 'New voicemail';
}

function statusBadgeClass(status: VoicemailQueueStatus) {
  if (status === 'reviewed') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (status === 'completed') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return 'bg-brand-500/15 text-brand-700 dark:text-brand-200';
}

function formatDuration(durationSeconds: number | null) {
  if (durationSeconds === null) return 'Duration unavailable';
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function nextQueueStatus(status: VoicemailQueueStatus): VoicemailQueueStatus | null {
  if (status === 'new') return 'reviewed';
  if (status === 'reviewed') return 'completed';
  return null;
}

function transcriptStatusLabel(status: 'pending' | 'completed' | 'failed') {
  if (status === 'completed') return 'Transcript ready';
  if (status === 'failed') return 'Transcript failed';
  return 'Transcript pending';
}

function nextActionLabel(status: VoicemailQueueStatus) {
  if (status === 'reviewed') return 'Next: complete follow-up in Comms.';
  if (status === 'completed') return 'Next: voicemail workflow complete.';
  return 'Next: assign an owner and review the recording.';
}

export function VoicemailsPage() {
  const [filter, setFilter] = useState<QueueFilter>('all');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queueQuery = useQuery({
    queryKey: ['voicemails', filter],
    queryFn: async () => {
      const response = await api.getVoicemails(filter === 'all' ? undefined : filter);
      return response.data;
    },
  });
  const ownersQuery = useQuery({
    queryKey: ['users', 'voicemail-owners'],
    queryFn: async () => {
      const response = await api.getUsers();
      return response.data.filter((user) => user.role === 'agent' || user.role === 'admin');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: {
      voicemailId: string;
      queueStatus?: VoicemailQueueStatus;
      assignedUserId?: string | null;
    }) => {
      await api.updateVoicemail(input.voicemailId, {
        ...(input.queueStatus !== undefined ? { queueStatus: input.queueStatus } : {}),
        ...(input.assignedUserId !== undefined ? { assignedUserId: input.assignedUserId } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['voicemails'] });
    },
  });

  const items = queueQuery.data ?? [];
  const ownerOptions = ownersQuery.data ?? [];

  return (
    <>
      <header className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground">Voicemails</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Review unanswered inbound calls, listen back, and jump straight into Comms.
        </p>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              filter === option.value
                ? 'border-brand-500 bg-brand-500/15 text-brand-700 dark:text-brand-200'
                : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mb-6 rounded-xl border border-border bg-card/70 p-4 text-sm text-muted-foreground">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium text-foreground">Voicemail queue playbook</p>
            <p className="mt-1">
              Work voicemail from this queue for now. The detailed operator flow is mirrored below
              and tracked in <code>docs/plans/2026-03-01-inbound-voice-operator-runbook.md</code>.
            </p>
          </div>
          <a
            href="#inbound-voice-runbook"
            className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            Open inbound voice runbook
          </a>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <p>1. New means the voicemail still needs triage.</p>
          <p>2. Reviewed means an owner has heard the recording and is handling follow-up.</p>
          <p>
            3. Completed means queue work is finished and any customer follow-up lives in Comms.
          </p>
          <p>
            Outbound notifications are not active yet. They remain planned until we ship an explicit
            alerting channel.
          </p>
        </div>
      </div>

      {queueQuery.isPending ? (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading voicemail queue...
        </div>
      ) : queueQuery.isError ? (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-300">
          Voicemail queue is unavailable right now.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/60 p-8 text-sm text-muted-foreground">
          No voicemail items match this filter.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const pendingStatus = nextQueueStatus(item.queueStatus);
            const ownerName =
              item.assignedUserName ??
              ownerOptions.find((user) => user.id === item.assignedUserId)?.name ??
              'Unassigned';

            return (
              <article key={item.id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(item.queueStatus)}`}
                    >
                      {statusLabel(item.queueStatus)}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {item.contactName ?? 'Unknown caller'}
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {item.contactPhone ?? 'Phone unavailable'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span>Reason: {item.routingReason.replace('_', ' ')}</span>
                      <span>{formatDuration(item.durationSeconds)}</span>
                      <span>Owner: {ownerName}</span>
                      <span>{transcriptStatusLabel(item.transcriptStatus)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {nextActionLabel(item.queueStatus)}
                    </p>
                    {item.recordingPlaybackUrl ? (
                      <a
                        href={item.recordingPlaybackUrl}
                        className="inline-flex text-sm font-medium text-brand-700 hover:text-brand-600 dark:text-brand-200 dark:hover:text-brand-100"
                      >
                        Play recording
                      </a>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="min-w-[12rem]">
                      <label className="block text-xs text-muted-foreground">
                        Assign owner
                        <select
                          aria-label="Assign owner"
                          value={item.assignedUserId ?? ''}
                          onChange={(event) =>
                            updateMutation.mutate({
                              voicemailId: item.id,
                              assignedUserId: event.target.value || null,
                            })
                          }
                          className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
                        >
                          <option value="">Unassigned</option>
                          {ownerOptions.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {pendingStatus ? (
                      <button
                        type="button"
                        onClick={() =>
                          updateMutation.mutate({
                            voicemailId: item.id,
                            queueStatus: pendingStatus,
                          })
                        }
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                      >
                        {pendingStatus === 'reviewed' ? 'Mark reviewed' : 'Mark completed'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          buildDialerPath({
                            conversation: item.conversationId,
                            contact: item.contactId,
                            phone: item.contactPhone,
                          }),
                        )
                      }
                      className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
                    >
                      Open in Comms
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div id="inbound-voice-runbook" className="mt-6 rounded-xl border border-border bg-card p-4">
        <p className="text-sm font-medium text-foreground">Inbound voice runbook</p>
        <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
          <p>1. Review new voicemail, assign an owner, and confirm the recording is playable.</p>
          <p>
            2. Use transcript status to decide whether to wait, retry later, or listen directly.
          </p>
          <p>3. Move the item to reviewed once someone owns the callback and context is clear.</p>
          <p>
            4. Jump into Comms for the actual customer follow-up, then mark the queue item
            completed.
          </p>
          <p>
            Future note: outbound notifications, SLA targets, and escalation timers are still
            planned rather than active.
          </p>
        </div>
      </div>
    </>
  );
}
